import { useEffect, useState } from "react";
import { Bell, ChevronDown, Gift, Wallet } from "lucide-react";
import type { AuthUser } from "../../hooks/useAuth";
import { useUSDTBalance, useVaultBalance, formatUSDT } from "../../hooks/useVault";
import { useSolanaMoney } from "../../hooks/useSolanaMoney";
import { useSolUsdPrice, formatUsd } from "../../hooks/useSolUsdPrice";
import { SOLANA_NETWORK, LAMPORTS_PER_SOL } from "../../contracts/solana/config";
import { useProfile } from "../../hooks/useProfile";
import { useFriends } from "../../store/friendsStore";
import Avatar from "../Avatar";
import ProfileDropdown from "../ProfileDropdown";

type NavTarget = "leaderboard" | "profile" | "tournaments" | "docs" | "stats" | "admin";

interface HeaderProps {
  isLoggedIn: boolean;
  /** When true, an "Admin" nav item appears (gated by ADMIN_WALLETS). */
  isAdmin?: boolean;
  walletAddress: string | null;
  firebaseUser: AuthUser | null;
  onShowAuthModal: () => void;
  onShowDeposit: () => void;
  onShowReferEarn: () => void;
  onShowFriends: () => void;
  onShowNotifications: () => void;
  onShowWallet: () => void;
  onShowSettings: () => void;
  onSignOut: () => void;
  onLogoClick: () => void;
  onNavigate?: (view: NavTarget) => void;
}

const NAV_LINKS: { label: string; href?: string; view?: NavTarget }[] = [
  { label: "Games",       href: "#games-grid" },
  { label: "Tournaments", view: "tournaments" },
  { label: "Leaderboard", view: "leaderboard" },
  { label: "Docs",        view: "docs" },
  { label: "Stats",       view: "stats" },
];

const Logo = () => (
  <svg viewBox="0 0 60 60" fill="none" className="w-8 h-8" aria-hidden="true">
    <path d="M6 6 L26 18 L26 42 L6 54 Z" stroke="#FF6B00" strokeWidth="5" strokeLinejoin="miter" />
    <path d="M54 6 L34 18 L34 42 L54 54 Z" stroke="#FF6B00" strokeWidth="5" strokeLinejoin="miter" />
  </svg>
);

// ── Headline balance ────────────────────────────────────────────────
// Split into two tiny siblings so rules-of-hooks holds: only one mounts
// (chosen by SOLANA_NETWORK), so a given component's hooks always run.
// Both render the same `.font-mono` pill markup; only the value differs.

/** EVM headline balance — embedded-wallet USDT + in-vault USDT → `$X`. */
function EvmHeaderBalance({ isLoggedIn, walletAddress }: { isLoggedIn: boolean; walletAddress: string | null }) {
  const usdt = useUSDTBalance(isLoggedIn ? walletAddress : null, 15_000);
  const vault = useVaultBalance(isLoggedIn ? walletAddress : null, 15_000);
  const totalBalance = usdt.value + vault.value;
  const balanceLabel = usdt.loading || vault.loading ? "…" : `$${formatUSDT(totalBalance)}`;
  return (
    <span className="font-mono text-[15px] leading-none tabular-nums font-semibold text-[var(--color-live)]">
      {balanceLabel}
    </span>
  );
}

/**
 * Last USD label shown per wallet, persisted so a reload paints the previous
 * balance immediately while the live RPC read + SOL price fetch are still in
 * flight (a couple of seconds); the live value replaces it as soon as both
 * land. Keyed by wallet so a different account never sees someone else's
 * number.
 */
const balanceCacheKey = (wallet: string) => `hupper:headerBalance:${wallet}`;

function readCachedBalance(wallet: string | null): string | null {
  if (!wallet) return null;
  try {
    return localStorage.getItem(balanceCacheKey(wallet));
  } catch {
    return null;
  }
}

/** Solana headline balance — on-chain Vault `available` (lamports) → USD `$X`. */
function SolanaHeaderBalance({ isLoggedIn, walletAddress }: { isLoggedIn: boolean; walletAddress: string | null }) {
  const { available, walletBalance } = useSolanaMoney(isLoggedIn ? walletAddress : null);
  const { price } = useSolUsdPrice();
  // Full balance = in-vault `available` (withdrawable) + un-deposited SOL still
  // in the Privy embedded wallet — the same "wallet + vault" sum the EVM header
  // shows. Keeps the headline honest before quick play moves funds into the
  // Vault, so the user never sees $0 right after depositing.
  // Wait for both the on-chain balance and the live price before quoting USD;
  // until then show the last persisted label for this wallet (fresh reload),
  // falling back to the same "…" placeholder as the EVM path.
  const lamports = available == null ? null : available + (walletBalance ?? 0n);
  const sol = lamports == null ? null : Number(lamports) / LAMPORTS_PER_SOL;
  const liveLabel = sol == null || price == null ? null : formatUsd(sol * price);
  useEffect(() => {
    if (liveLabel == null || !walletAddress) return;
    try {
      localStorage.setItem(balanceCacheKey(walletAddress), liveLabel);
    } catch {
      /* localStorage unavailable — cache is best-effort */
    }
  }, [liveLabel, walletAddress]);
  const balanceLabel = liveLabel ?? readCachedBalance(walletAddress) ?? "…";
  return (
    <span className="font-mono text-[15px] leading-none tabular-nums font-semibold text-[var(--color-live)]">
      {balanceLabel}
    </span>
  );
}

export default function Header({
  isLoggedIn,
  isAdmin,
  walletAddress,
  firebaseUser,
  onShowAuthModal,
  onShowDeposit,
  onShowReferEarn,
  onShowFriends,
  onShowNotifications,
  onShowWallet,
  onShowSettings,
  onSignOut,
  onLogoClick,
  onNavigate,
}: HeaderProps) {
  // Headline balance is rendered by a gated sibling component (Evm/Solana
  // HeaderBalance) so its money hooks only run on the active network.

  // Unread notifications = pending friend requests + incoming match
  // challenges. Drives the badge on the bell so they're visible without
  // opening the panel. (friendsStore is empty when logged out.)
  const friends = useFriends();
  const notifCount =
    friends.incoming.length +
    friends.matchInvitesIn.length +
    friends.staffMessages.filter((m) => !m.readAt).length;
  const notifLabel = notifCount > 9 ? "9+" : String(notifCount);
  const [profileOpen, setProfileOpen] = useState(false);
  // On Solana, "Deposit" copies the wallet address AND opens the wallet
  // panel (the balance card) — no deposit screen. Quick Play's auto-sweep
  // makes anything sent to it playable. EVM keeps its DepositModal flow.
  const [depositCopied, setDepositCopied] = useState(false);
  const handleDeposit = () => {
    if (SOLANA_NETWORK && walletAddress) {
      navigator.clipboard.writeText(walletAddress).catch(() => {});
      setDepositCopied(true);
      setTimeout(() => setDepositCopied(false), 1500);
    }
    onShowDeposit();
  };
  const { profile, avatarSrc } = useProfile();
  const avatarHandle = profile?.handle ?? firebaseUser?.displayName ?? null;

  return (
    <header className="sticky top-0 z-40 bg-[var(--color-canvas)]/85 backdrop-blur-md hairline-b">
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-[70px] flex items-center justify-between gap-8">

        {/* ── Logo ── */}
        <button
          onClick={onLogoClick}
          className="flex items-center gap-2 group focus:outline-none"
          aria-label="hupper home"
        >
          <Logo />
          <span className="text-[21px] font-semibold tracking-[-0.02em] lowercase text-[var(--color-text-primary)] group-hover:text-[var(--color-brand)] transition-colors duration-150">
            hupper
          </span>
        </button>

        {/* ── Nav (desktop) ── */}
        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((link) =>
            link.view ? (
              <button
                key={link.label}
                onClick={() => onNavigate?.(link.view!)}
                className="px-3 py-1.5 text-[14px] font-medium text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] rounded-md transition-colors duration-150"
              >
                {link.label}
              </button>
            ) : (
              <a
                key={link.label}
                href={link.href}
                className="px-3 py-1.5 text-[14px] font-medium text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] rounded-md transition-colors duration-150"
              >
                {link.label}
              </a>
            ),
          )}
          {isAdmin && (
            <button
              onClick={() => onNavigate?.("admin")}
              className="px-3 py-1.5 text-[14px] font-semibold rounded-md transition-colors duration-150"
              style={{ color: "#FF6B00" }}
            >
              Admin
            </button>
          )}
        </nav>

        {/* ── Right cluster ── */}
        <div className="flex items-center gap-3">

          {/* CTAs / User */}
          {isLoggedIn ? (
            <div className="flex items-center gap-2.5 sm:gap-3">
              <button
                type="button"
                onClick={() => onNavigate?.("profile")}
                className="hidden sm:flex flex-col items-start leading-tight"
                aria-label="Open profile"
              >
                <span className="text-[11px] font-medium text-[var(--color-text-tertiary)]">Balance</span>
                {SOLANA_NETWORK ? (
                  <SolanaHeaderBalance isLoggedIn={isLoggedIn} walletAddress={walletAddress} />
                ) : (
                  <EvmHeaderBalance isLoggedIn={isLoggedIn} walletAddress={walletAddress} />
                )}
              </button>

              <button
                type="button"
                onClick={handleDeposit}
                title={SOLANA_NETWORK ? "Copy your address to receive SOL" : undefined}
                className="inline-flex items-center justify-center rounded-md px-3.5 py-2 bg-[var(--color-brand)] hover:bg-[var(--color-brand-hover)] text-[#0A0A0B] font-semibold text-[13px] transition-colors duration-150"
                style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.20)" }}
              >
                {depositCopied ? "Copied ✓" : "Deposit"}
              </button>

              <button
                type="button"
                onClick={onShowReferEarn}
                aria-label="Refer & earn"
                className="hidden sm:inline-flex w-8 h-9 items-center justify-center rounded-md text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface)] transition-colors duration-150"
              >
                <Gift className="w-5 h-5" strokeWidth={2} />
              </button>

              <button
                type="button"
                onClick={onShowNotifications}
                aria-label={notifCount > 0 ? `Notifications (${notifCount} unread)` : "Notifications"}
                className="relative hidden sm:inline-flex w-8 h-9 items-center justify-center rounded-md text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface)] transition-colors duration-150"
              >
                <Bell className="w-5 h-5" strokeWidth={2} />
                {notifCount > 0 && (
                  <span
                    aria-hidden="true"
                    className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 inline-flex items-center justify-center rounded-full bg-[#FF6B00] text-black text-[10px] leading-none font-bold font-mono tabular-nums ring-2 ring-[var(--color-canvas)]"
                  >
                    {notifLabel}
                  </span>
                )}
              </button>

              <div className="relative">
              <button
                type="button"
                onClick={() => setProfileOpen((v) => !v)}
                aria-label="Open profile menu"
                className="flex items-center gap-2 rounded-full pl-1 pr-1.5 py-1 hover:bg-[var(--color-surface)] transition-colors duration-150"
              >
                <Avatar address={walletAddress} handle={avatarHandle} src={avatarSrc} size={36} />
                <ChevronDown className="w-4 h-4 text-[var(--color-text-tertiary)]" strokeWidth={2.25} />
              </button>
              {profileOpen && (
                <ProfileDropdown
                  user={firebaseUser}
                  walletAddress={walletAddress}
                  onClose={() => setProfileOpen(false)}
                  onShowWallet={onShowWallet}
                  onShowProfile={() => onNavigate?.("profile")}
                  onShowLeaderboard={() => onNavigate?.("leaderboard")}
                  onShowReferEarn={onShowReferEarn}
                  onShowFriends={onShowFriends}
                  onShowSettings={onShowSettings}
                  onSignOut={onSignOut}
                />
              )}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              <button
                onClick={onShowAuthModal}
                className="hidden sm:inline-flex px-3 py-1.5 text-[13px] font-medium text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] rounded-md transition-colors duration-150"
              >
                Sign in
              </button>
              <button
                onClick={onShowAuthModal}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md bg-[var(--color-brand)] hover:bg-[var(--color-brand-hover)] text-[#0A0A0B] font-semibold text-[13px] tracking-tight transition-colors duration-150"
                style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.20)" }}
              >
                <Wallet className="w-3.5 h-3.5" strokeWidth={2.5} />
                Connect Wallet
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
